import { MONTH_NAMES } from "./date-time";

/** Sunday first, two letters wide so seven columns fit a narrow sidebar. */
export const WEEKDAY_LABELS = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"] as const;
export const CALENDAR_WEEKS = 6;

/**
 * Six weeks of dates for one month, always starting on a Sunday. The grid keeps its height
 * whatever month it shows, so flipping months never moves the rows under the pointer, and
 * the days it borrows from the months either side are real dates with notes of their own.
 */
export function monthGrid(year: number, month: number): Date[][] {
  const first = new Date(year, month, 1);
  const start = new Date(year, month, 1 - first.getDay());
  return Array.from({ length: CALENDAR_WEEKS }, (_, week) =>
    Array.from(
      { length: WEEKDAY_LABELS.length },
      (_, day) => shiftDay(start, week * WEEKDAY_LABELS.length + day),
    ),
  );
}

export function monthLabel(year: number, month: number): string {
  const date = new Date(year, month, 1);
  return `${MONTH_NAMES[date.getMonth()]!.toUpperCase()} ${date.getFullYear()}`;
}

/**
 * The month `months` away, and the day in it closest to `day`: stepping off the 31st into a
 * shorter month lands on its last day rather than spilling into the next one.
 */
export function shiftMonth(
  year: number,
  month: number,
  months: number,
  day = 1,
): { year: number; month: number; day: number } {
  const moved = new Date(year, month + months, 1);
  const length = new Date(moved.getFullYear(), moved.getMonth() + 1, 0).getDate();
  return {
    year: moved.getFullYear(),
    month: moved.getMonth(),
    day: Math.min(day, length),
  };
}

/** Counted in whole days, so an hour lost or gained to daylight saving cannot skip one. */
export const shiftDay = (date: Date, days: number): Date =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);

export const sameDay = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();
